import { useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { toast } from "../../common/components/Toast";
import { SPACING } from "../../common/constants/spacing";
import { useTheme } from "../../common/contexts/ThemeContext";
import { uploadFile } from "../api/files";

const FileUploadInput = ({ setFiles, onFileUploaded }) => {
  const { t } = useTranslation("sources");
  const [uploading, setUploading] = useState(false);
  const { colors } = useTheme();

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];

    setUploading(true);
    try {
      const response = await uploadFile(file);
      if (response.requires_mapping) {
        // File uploaded successfully, now needs column mapping
        toast.success(t("fileUpload.success"));
        onFileUploaded(response);
      } else if (response.report && response.report.status === "success") {
        // Legacy flow - file processed immediately
        toast.success(response.report.message);
        setFiles((prevFiles) => [response.file, ...prevFiles]);
      } else {
        toast.error(response.report?.message || "Upload failed");
      }
    } catch (error) {
      toast.error(t("fileUpload.error", { message: error.message }));
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "text/csv": [".csv"],
    },
  });

  return (
    <div>
      <div
        {...getRootProps()}
        className="text-center"
        style={{
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: SPACING.xl,
          borderRadius: "6px",
          border: isDragActive
            ? `2px dashed ${colors.primary}`
            : `1px dashed ${colors.border.secondary}`,
          backgroundColor: isDragActive
            ? "rgba(54, 140, 226, 0.05)"
            : "transparent",
          transition: "all 0.2s ease-in-out",
        }}
      >
        <input {...getInputProps()} disabled={uploading} />
        {uploading ? (
          <div>
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 txt-primary fw-bold">
              {t("manualImport.fileUpload.processingReports")}
            </p>
          </div>
        ) : (
          <div>
            <p className="fw-bold txt-lighter">
              {isDragActive
                ? t("fileUpload.dropZone.active")
                : t("fileUpload.dropZone.inactive")}
            </p>
            <p className="small txt-lighter">
              {t("manualImport.fileUpload.supportedFormats")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploadInput;
